import { IDocument } from 'interfaces/documentation/document';
import { InboxOutlined } from '@ant-design/icons';
import {
  Modal,
  ModalProps,
  Space,
  Upload,
  UploadFile,
  UploadProps,
  message
} from 'antd';
import {
  createDocument,
  presignUpload,
  updateDocument
} from 'requests/documentations';
import { uploadFileToS3 } from 'services/aws-s3';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

const { Dragger } = Upload;

export interface ModalUploadDocumentProps {
  modalClose: () => void;
  modalProps: ModalProps;
  document?: IDocument;
  templateId?: string;
  refetch?: () => void;
}

export const ModalUploadDocument = ({
  modalProps,
  document,
  templateId,
  modalClose,
  refetch
}: ModalUploadDocumentProps) => {
  const { id } = useParams();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(async () => {
    if (fileList.length === 0) {
      return;
    }

    const file = fileList[0];
    setUploading(true);

    try {
      const { presigned_url, path } = await presignUpload(id!);

      await uploadFileToS3(file as unknown as File, presigned_url).then(
        async () => {
          if (!document) {
            await createDocument({
              template_id: templateId!,
              documentationId: id!,
              file_path: path,
              file_name: file.name,
              file_size: file.size ?? 0
            });

            message.success('Documento criado com sucesso');
          } else {
            await updateDocument({
              documentationId: id!,
              id: document.id,
              file_path: path,
              file_name: file.name,
              file_size: file.size ?? 0
            });

            message.success('Upload do documento realizado com sucesso');
          }
        }
      );

      refetch?.();
      modalClose();
      setFileList([]);
    } catch {
      message.error(
        'Não foi possível realizar o upload do documento. Tente novamente.'
      );
    } finally {
      setUploading(false);
    }
  }, [document, id, modalClose, refetch, templateId, fileList]);

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: file => {
      const isPDF = file.type === 'application/pdf';
      const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg';
      const isPNG = file.type === 'image/png';
      const isValidType = isPDF || isJPG || isPNG;

      if (!isValidType) {
        message.error(
          `${file.name} não é um arquivo válido. Apenas PDF, JPG ou PNG são aceitos.`
        );
        return false;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB

      if (file.size > 0 && file.size > maxSize) {
        message.error(`${file.name} é muito grande. O tamanho máximo é 10MB.`);
        return false;
      }

      setFileList([file]);
      return false;
    },
    onRemove: file => {
      const index = fileList.indexOf(file);
      const newFileList = [...fileList];
      newFileList.splice(index, 1);
      setFileList(newFileList);
    }
  };

  return (
    <Modal
      style={{ maxWidth: 412 }}
      {...modalProps}
      title="Importar novo documento"
      cancelText="Cancelar"
      okText="Importar"
      onOk={handleUpload}
      className="!w-[1022px]"
      destroyOnClose
      confirmLoading={uploading}
      okButtonProps={{ disabled: fileList.length === 0 }}
    >
      <Space direction="vertical" className="w-full">
        <Dragger {...props}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Clique ou arraste o arquivo para esta área para fazer upload
          </p>
          <p className="ant-upload-hint">
            Importar um único arquivo PDF, JPG ou PNG
          </p>
        </Dragger>
      </Space>
    </Modal>
  );
};
